import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import PermissionGate from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarRange, Plus, Pencil, Trash2, Search, Users } from "lucide-react";

const EMPTY = {
  name: "", course_id: "", branch_id: "", trainer_id: "", batch_type: "Weekday",
  mode: "Offline", timing: "", start_date: "", end_date: "", seats: 0, status: "Upcoming",
};
const STATUS_STYLE = {
  Upcoming: "bg-blue-100 text-blue-700", Ongoing: "bg-accent/10 text-accent",
  Completed: "bg-slate-100 text-slate-600", Cancelled: "bg-red-100 text-red-600",
};

const seatHint = (b) => {
  const seats = Number(b.seats || 0);
  if (!seats) return <span className="ml-1 text-[11px] text-muted-foreground">open</span>;
  const left = seats - Number(b.enrolled_count || 0);
  if (left <= 0) return <span className="ml-1 rounded bg-red-100 px-1.5 text-[11px] font-medium text-red-600">Full</span>;
  if (left <= 3) return <span className="ml-1 rounded bg-amber-100 px-1.5 text-[11px] font-medium text-amber-700">{left} left</span>;
  return <span className="ml-1 text-[11px] text-accent">{left} left</span>;
};

export default function Batches() {
  const [rows, setRows] = useState([]);
  const [courses, setCourses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [meta, setMeta] = useState({ types: [], modes: [], statuses: [] });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/batches", { params: { search: search || undefined, status: statusFilter } });
    setRows(data.items || []);
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/courses", { params: { active_only: true } }).then(({ data }) => setCourses(data.items || []));
    api.get("/branches").then(({ data }) => setBranches(data || []));
    api.get("/trainers", { params: { active_only: true } }).then(({ data }) => setTrainers(data.items || []));
    api.get("/batches/meta").then(({ data }) => setMeta(data));
  }, []);

  const openNew = () => { setForm({ ...EMPTY, course_id: courses[0]?.id || "" }); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...EMPTY, ...b }); setEditId(b.id); setOpen(true); };

  const save = async () => {
    if (!form.course_id) { toast.error("Please select a course"); return; }
    setSaving(true);
    try {
      const payload = { ...form, seats: Number(form.seats) };
      if (editId) await api.put(`/batches/${editId}`, payload);
      else await api.post("/batches", payload);
      toast.success(editId ? "Batch updated" : "Batch created");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete batch "${b.name}"?`)) return;
    try { await api.delete(`/batches/${b.id}`); toast.success("Batch deleted"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const sel = (k) => (v) => setForm((f) => ({ ...f, [k]: v === "none" ? "" : v }));

  return (
    <div data-testid="batches-page">
      <PageHeader title="Batches" subtitle="Schedule and manage training batches." />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search batches..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-64 pl-9" data-testid="batch-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-44" data-testid="batch-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <PermissionGate permission="batch.create">
          <Button onClick={openNew} data-testid="add-batch-button"><Plus className="mr-1.5 h-4 w-4" /> Add batch</Button>
        </PermissionGate>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Batch</TableHead><TableHead>Course</TableHead>
              <TableHead>Trainer</TableHead><TableHead>Type / Mode</TableHead><TableHead>Seats</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">No batches yet.</TableCell></TableRow>}
            {rows.map((b) => (
              <TableRow key={b.id} data-testid={`batch-row-${b.batch_code}`}>
                <TableCell className="font-medium">{b.batch_code}</TableCell>
                <TableCell className="flex items-center gap-2 font-medium text-secondary"><CalendarRange className="h-4 w-4 text-primary" />{b.name}</TableCell>
                <TableCell>{b.course_name || "—"}</TableCell>
                <TableCell>{b.trainer_name || "—"}</TableCell>
                <TableCell className="text-sm">{b.batch_type}<br /><span className="text-muted-foreground">{b.mode}</span></TableCell>
                <TableCell><span className="flex items-center gap-1 text-sm"><Users className="h-3.5 w-3.5 text-muted-foreground" />{b.enrolled_count || 0}/{b.seats}</span>{seatHint(b)}</TableCell>
                <TableCell><Badge className={`border-0 ${STATUS_STYLE[b.status] || ""}`}>{b.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <PermissionGate permission="batch.update"><Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-batch-${b.batch_code}`}><Pencil className="h-4 w-4" /></Button></PermissionGate>
                  <PermissionGate permission="batch.delete"><Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(b)} data-testid={`delete-batch-${b.batch_code}`}><Trash2 className="h-4 w-4" /></Button></PermissionGate>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="batch-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit batch" : "Add batch"}</DialogTitle>
            <DialogDescription>Schedule the batch and assign course, branch and trainer.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Batch name</Label><Input value={form.name} onChange={set("name")} data-testid="batch-name-input" /></div>
            <div>
              <Label>Course</Label>
              <Select value={form.course_id} onValueChange={sel("course_id")}>
                <SelectTrigger data-testid="batch-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Branch</Label>
              <Select value={form.branch_id || "none"} onValueChange={sel("branch_id")}>
                <SelectTrigger data-testid="batch-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Trainer</Label>
              <Select value={form.trainer_id || "none"} onValueChange={sel("trainer_id")}>
                <SelectTrigger data-testid="batch-trainer-select"><SelectValue placeholder="Select trainer" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.batch_type} onValueChange={(v) => setForm((f) => ({ ...f, batch_type: v }))}>
                <SelectTrigger data-testid="batch-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
                <SelectTrigger data-testid="batch-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="batch-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Timing</Label><Input value={form.timing} onChange={set("timing")} placeholder="10:00 AM - 1:00 PM" /></div>
            <div><Label>Seats</Label><Input type="number" value={form.seats} onChange={set("seats")} data-testid="batch-seats-input" /></div>
            <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={set("start_date")} /></div>
            <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={set("end_date")} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-batch-button">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
